import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Keyboard, A11y } from 'swiper';
import InsightCard from './cards/InsightCard';
import { device, shadows } from '../../utils/styled-component-mixins';
import { useLocalization } from '../custom-hooks/useLocalization';
import { withLocalization } from '../shared/Context/LocalizationContext';
import ServerStylesConditionalWrapper from '../shared/ServerStylesConditionalWrapper';
import NoResultsCard from '../shared/NoResultsCard';
import useMediaQuery from '../custom-hooks/useMediaQuery';
import { withWidgetSpacingWrapper } from '../shared/WidgetSpacingWrapper';
import WidgetIdWrapper from '../shared/WidgetIdWrapper';
import { compose } from '../utils/compose';
import EventWebinarCard from './cards/EventWebinarCard/EventWebinarCard';
import GenericCard from './cards/GenericCard';
import CardSwiperWrapperStyled from './styled-components/CardSwiperWrapperStyled';

const SearchPreviewStyled = styled.div`
  .grid-layout {
    .card-grid {
      display: grid;
      grid-template-columns: 100%;
      grid-template-rows: max-content;
      grid-gap: 1.6rem;
      grid-template-columns: repeat(auto-fill, minmax(35rem, 1fr));

      > .widget-spacing-wrapper {
        flex: 0 1 24%;
      }

      .event-webinar-card-wrapper {
        width: 100%;
        ${shadows.cardShadow}

        @media ${device.desktop} {
          min-height: 54rem;
        }
      }

      .widget-spacing-wrapper {
        margin-bottom: 0;
      }

      .insight-card-wrapper,
      .insight-card {
        height: 100%;
      }

      .overflow-wrapper > a {
        text-decoration: none;
      }

      .no-results-card {
        z-index: 1;
      }
    }

    .advanced-search-cta {
      align-items: center;
      display: flex;
      margin-top: 4rem;
      margin-bottom: 8.3rem;
      right: 11rem;
      flex-direction: row;

      @media ${device.desktop} {
        justify-content: flex-end;
        margin-bottom: 4rem;
        right: 7rem;
      }

      @media ${device.deviceOnly} {
        justify-content: center;
        right: 0;
        width: 100%;
        margin-bottom: 0;
      }

      a,
      p {
        font-size: 1.8rem;
        line-height: 2.4rem;
      }

      p {
        margin: 0;

        @media ${device.desktop} {
          margin-bottom: 1px;
          margin-right: 0.8rem;
        }
      }
    }
  }

  .carousel-layout {
    .event-webinar-card-wrapper {
      width: 100%;
    }

    .container-padding-md {
      @media ${device.deviceOnly} {
        padding: 0;
      }
    }

    .swiper {
      .swiper-slide {
        box-shadow: none;

        @media ${device.desktop} {
          max-width: 25%;
        }

        > div {
          margin-bottom: 0;
          height: 100%;

          .insight-card-wrapper {
            height: 100%;
          }

          .insight-card {
            height: 100%;
            display: flex;
            flex-direction: column;

            .featured-card-content {
              flex: 1 1 auto;

              @media ${device.desktop} {
                min-height: 31.2rem;
              }

              .animation-wrapper {
                display: flex;
                flex-direction: column;
                justify-content: space-between;
                height: 100%;
              }
            }
          }
        }
      }
    }

    .no-results-wrapper {
      .no-results-card {
        min-height: 64.5rem;
        width: 43.7rem;
      }
    }

    .no-results-wrapper {
      .no-results-card {
        min-height: 64.5rem;
        width: 43.7rem;
      }
    }
  }

  .insight-card {
    display: flex;
    flex-direction: column;
    height: 100%;

    .animation-wrapper {
      align-items: flex-start;
      flex-direction: column !important;
      justify-content: space-between !important;
      display: flex;
      height: 100%;

      @media ${device.desktop} {
        align-items: flex-start;
        justify-content: flex-end;
      }

      .overflow-wrapper {
        align-self: flex-start;
      }
    }
  }

  .featured-card-content {
    flex: 1 1 auto;
    min-height: 22.4rem;

    @media ${device.desktop} {
      min-height: 31.2rem;
    }
  }

  .event-webinar-card-wrapper,
  .generic-card-wrapper {
    .animation-wrapper {
      p {
        @media ${device.deviceOnly} {
          font-size: 1.4rem;
        }
      }
    }
  }

  .insight-card,
  .event-webinar-card-wrapper,
  .generic-card-wrapper {
    h3 {
      font-weight: 800;

      @media ${device.deviceOnly} {
        font-size: 2rem;
        margin-bottom: 1.2rem;
      }
    }
  }

  .generic-card-wrapper {
    ${shadows.cardShadow};
    height: 100%;

    .generic-card-content {
      @media ${device.deviceOnly} {
        padding: 2.4rem;
      }
    }

    @media ${device.desktop} {
      min-height: 54rem;
    }

    .animation-wrapper {
      &.hover-in {
        .overflow-wrapper {
          a {
            &:after {
              width: 100%;
            }
          }
        }
      }
    }

    .overflow-wrapper {
      a {
        border-bottom: 2px solid transparent;
        margin-right: 3.2rem;
        position: relative;
        transition: background-size 0.3s;

        &::after {
          background-color: var(--button-tertiary);
          bottom: 0;
          content: '';
          display: block;
          height: 2px;
          left: 0;
          position: absolute;
          transition: width 0.3s;
          width: 0;
        }

        span[class*='material-icons'] {
          position: absolute;
          left: 100%;
        }
      }
    }
  }

  .heading-wrapper {
    margin-bottom: 4rem;

    @media ${device.desktop} {
      margin-bottom: 4rem;
    }

    h2 {
      font-size: 3rem;
      font-weight: normal;
      line-height: 3.2rem;
      padding: 0 1.5rem;
      position: relative;
      text-align: center;
      z-index: 1;

      @media ${device.desktop} {
        font-size: 3.6rem;
        line-height: 4.8rem;
      }
    }

    .sub-heading {
      margin: 1.6rem auto 0 auto;
      max-width: 70rem;
      padding: 0 1.5rem;
      position: relative;
      text-align: center;
      z-index: 1;

      @media ${device.desktop} {
        font-size: 2rem;
        line-height: 3.2rem;
      }
    }
  }

  .event-webinar-card-wrapper {
    @media ${device.deviceOnly} {
      .featured-card-content {
        padding: 2.4rem 1.6rem;
      }
    }

    .event-time-location {
      @media ${device.deviceOnly} {
        margin-bottom: 1rem;
      }
    }

    .event-right-column {
      p {
        display: block;
      }
    }
  }
`;

const SearchPreviewComponent = (props) => {
  const isDesktop = useMediaQuery(device.desktop);
  const isMobile = useMediaQuery(device.mobileOnly);
  const { resourceStrings } = useLocalization();
  const noResultsMessage = resourceStrings['bdO.InsightSectionWidget.NoResults'];
  const insightReadMoreText = resourceStrings['bdO.FeaturedInsight.LinkText'];
  const eventRegisterText = resourceStrings['bdO.EventsWebinars.Register'];
  const featuredTagText = resourceStrings['bdO.FeaturedCard.FeaturedTagText'];
  const { displayLayout, background } = props.properties;
  const { searchResults } = props;

  const cardProps = {
    layout: 'standard',
    mediaSide: 'left',
    featuredTagText
  };

  const ResultCard = ({ result }) => {
    if (result.classname.toLowerCase() === 'bdo.insightnode') {
      return (
        <InsightCard
          item={{
            thumbnail1: result.thumbnailImage,
            primaryCategory: result.primaryCategory,
            pageTitle: result.title,
            description: result.description,
            url: result.url,
            readMoreText: insightReadMoreText,
            publishDate: result.publishDate,
            isFeatured: result.isFeatured,
            externalUrl: result.externalUrl
          }}
          cardProps={cardProps}
          key={result.nodeGuid}
        />
      );
    }
    if (result.classname.toLowerCase() === 'bdo.eventwebinarnode') {
      return (
        <EventWebinarCard
          key={result.nodeGuid}
          city={result.city}
          state={result.eventState}
          dateFrom={result.startDateTime}
          description={result.description}
          pageTitle={result.title}
          eventType={result.eventType}
          slug={result.url}
          cardCtaText={eventRegisterText}
          timeZone={result.timeZone}
          isFeatured={result.isFeatured}
        />
      );
    }
    return (
      <GenericCard
        key={result.nodeGuid}
        item={{
          pageTitle: result.title,
          description: result.description,
          url: result.url,
          isFeatured: result.isFeatured,
          readMoreText: insightReadMoreText,
          featuredTagText
        }}
      />
    );
  };

  const GridLayout = () => (
    <div className="grid-layout" data-ktc-search-exclude>
      <div className="card-grid-wrapper container-padding-md">
        {(props.properties.heading || props.properties.description) && (
          <div className="heading-wrapper">
            {props.properties.heading && <h2>{props.properties.heading}</h2>}
            {props.properties.description && (
              <p className="sub-heading">{props.properties.description}</p>
            )}
          </div>
        )}
        <div className="card-grid">
          {searchResults && (
            <>
              {searchResults.map((result) => (
                <ResultCard result={result} />
              ))}
            </>
          )}
        </div>
        {props.properties.exploreMoreLinkText && props.properties.exploreMoreLinkURL && (
          <div className="advanced-search-cta">
            <ButtonLink
              text={props.properties.exploreMoreLinkText}
              link={props.properties.exploreMoreLinkURL}
              target="_self"
              type="tertiary"
              theme="light"
            />
          </div>
        )}
        {!searchResults && (
          <div>
            <NoResultsCard className="no-results-card" noResultsMessage={noResultsMessage} />
          </div>
        )}
      </div>
    </div>
  );

  const CarouselLayout = () => (
    <div className="carousel-layout" data-ktc-search-exclude>
      {(props.properties.heading || props.properties.description) && (
        <div className="heading-wrapper">
          {props.properties.heading && <h2>{props.properties.heading}</h2>}
          {props.properties.description && (
            <p className="sub-heading">{props.properties.description}</p>
          )}
        </div>
      )}
      <CardSwiperWrapperStyled backgroundImage={background}>
        <div className="container-padding-md">
          <div className="card-slider">
            <Swiper
              pagination={{ clickable: { isDesktop } }}
              modules={[Pagination, Navigation, Keyboard, A11y]}
              a11y={true}
              navigation={true}
              keyboard={true}
              breakpoints={{
                320: {
                  centeredSlides: true,
                  slidesPerView: 'auto',
                  slidesPerGroup: 1,
                  spaceBetween: 16
                },
                992: {
                  centeredSlides: false,
                  slidesPerView: 4,
                  slidesPerGroup: 4,
                  spaceBetween: 16
                },
                1921: {
                  centeredSlides: false,
                  slidesPerGroup: 5,
                  slidesPerView: 5,
                  spaceBetween: 16
                }
              }}
            >
              {searchResults &&
                searchResults.map((result) => (
                  <SwiperSlide key={result.title}>
                    <ResultCard result={result} />
                  </SwiperSlide>
                ))}
            </Swiper>
            {props.properties.exploreMoreLinkText && props.properties.exploreMoreLinkURL && (
              <div className="related-resources-cta">
                <ButtonLink
                  text={props.properties.exploreMoreLinkText}
                  link={props.properties.exploreMoreLinkURL}
                  target="_self"
                  type="tertiary"
                  theme="light"
                />
              </div>
            )}
          </div>
        </div>
      </CardSwiperWrapperStyled>
    </div>
  );

  const PREVIEW_LAYOUTS = {
    grid: <GridLayout />,
    carousel: <CarouselLayout />
  };

  return (
    <WidgetIdWrapper widgetID={props.properties.widgetID}>
      <ServerStylesConditionalWrapper>
        <SearchPreviewStyled>
          {isMobile ? <CarouselLayout /> : PREVIEW_LAYOUTS[displayLayout]}
        </SearchPreviewStyled>
      </ServerStylesConditionalWrapper>
    </WidgetIdWrapper>
  );
};

export default compose(withLocalization, withWidgetSpacingWrapper)(SearchPreviewComponent);
